import { Component, signal, computed, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule }  from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// MODEL — matches contact_msgs
// ──────────────────────────────────────────────────────────
export interface ContactMsg {
  id:         number;
  full_name:  string;
  email_id:   string;
  mobile_no:  string;
  msg_type:   string;
  subject:    string;
  message:    string;
  status:     string;
  req_date:   Date;
  is_active:  boolean;
}

export type MsgStatus = 'New' | 'In Progress' | 'Replied' | 'Closed';
export type MsgType   = 'Enquiry' | 'Admission' | 'Complaint' | 'Feedback' | 'Support' | 'Other';

// ──────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────
@Component({
  selector:    'app-contact-messages',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  providers: [DatePipe],
  templateUrl: './contact-messages.html',
  styleUrl:    './contact-messages.scss',
})
export class ContactMessages {
  dataService = inject(DataService);
	datePipe = inject(DatePipe);
  base = 'contact-msg';		// important for API calls
	loading = false;
	// isEdit = false;
	// allData: any = [];
	today = new Date().toISOString().split('T')[0];
	frmData: any = {};

  // ── UI state ─────────────────────────────────────────────
  showFormModal   = signal(false);
  showViewModal   = signal(false);
  showDeleteModal = signal(false);
  isEditing       = signal(false);
  submitted       = false;
  saveSuccess     = signal('');
  viewTarget      = signal<ContactMsg | null>(null);
  deleteTarget    = signal<ContactMsg | null>(null);

  // ── Filters & Pagination (signals so computeds re-evaluate) ─
  searchQuery  = signal('');
  filterStatus = signal<'all' | MsgStatus>('all');
  filterType   = signal<'all' | MsgType>('all');
  filterActive = signal<'all' | 'active' | 'inactive'>('all');
  currentPage  = signal(1);
  pageSize     = signal(10);
  sortField    = signal<keyof ContactMsg>('req_date');
  sortAsc      = signal(false);

  readonly msgTypes: MsgType[]     = ['Enquiry', 'Admission', 'Complaint', 'Feedback', 'Support', 'Other'];
  readonly statusOptions: MsgStatus[] = ['New', 'In Progress', 'Replied', 'Closed'];

  // ── Form model ───────────────────────────────────────────
  form: Omit<ContactMsg, 'id'> = this.blankForm();
  errors: Partial<Record<keyof Omit<ContactMsg, 'id' | 'req_date' | 'is_active'>, string>> = {};
  private editingId = 0;

  // ── Data ─────────────────────────────────────────────────
  private nextId = 21;
  messages = signal<ContactMsg[]>([]);        //(this.buildFeed());

  // ── Computed ─────────────────────────────────────────────
  readonly filtered = computed(() => {
    const q      = this.searchQuery().toLowerCase().trim();
    const status = this.filterStatus();
    const type   = this.filterType();
    const active = this.filterActive();
    const field  = this.sortField();
    const asc    = this.sortAsc();

    let list = this.messages().filter(m => {
      const matchQ = !q || [
        m.full_name, m.email_id, m.mobile_no, m.msg_type, m.subject, m.message, m.status,
      ].some(v => v.toLowerCase().includes(q));
      const matchStatus = status === 'all' || m.status === status;
      const matchType   = type === 'all' || m.msg_type === type;
      const matchActive =
        active === 'all'      ? true :
        active === 'active'   ? m.is_active :
                                !m.is_active;
      return matchQ && matchStatus && matchType && matchActive;
    });

    list = [...list].sort((a, b) => {
      const av = a[field];
      const bv = b[field];
      if (av instanceof Date && bv instanceof Date) {
        return asc ? av.getTime() - bv.getTime() : bv.getTime() - av.getTime();
      }
      if (typeof av === 'boolean' && typeof bv === 'boolean') {
        return asc ? Number(av) - Number(bv) : Number(bv) - Number(av);
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return asc ? av - bv : bv - av;
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      return asc ? as.localeCompare(bs) : bs.localeCompare(as);
    });

    return list;
  });

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filtered().length / this.pageSize())));

  readonly paged = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filtered().slice(start, start + this.pageSize());
  });

  readonly pageNumbers = computed(() => {
    const t = this.totalPages(), c = this.currentPage();
    const pages: number[] = [];
    for (let i = Math.max(1, c - 2); i <= Math.min(t, c + 2); i++) pages.push(i);
    return pages;
  });

  readonly stats = computed(() => {
    const list = this.messages().filter(m => m.is_active);
    return {
      total:      list.length,
      new:        list.filter(m => m.status === 'New').length,
      progress:   list.filter(m => m.status === 'In Progress').length,
      replied:    list.filter(m => m.status === 'Replied').length,
      closed:     list.filter(m => m.status === 'Closed').length,
    };
  });

  ngOnInit() {
		this.loading = true;
    this.dataService.getAllRecords(this.base).then((res: any) => {
      if(res && res.length) {
        this.messages.set(res);
      }
      // this.allData = res;
      this.loading = false;
    });
	}

  // ──────────────────────────────────────────────────────────
  // CRUD
  // ──────────────────────────────────────────────────────────
  openAdd(): void {
    this.form      = this.blankForm();
    this.errors    = {};
    this.submitted = false;
    this.isEditing.set(false);
    this.editingId = 0;
    this.showFormModal.set(true);
  }

  openEdit(m: ContactMsg): void {
    this.form = {
      full_name: m.full_name,
      email_id:  m.email_id,
      mobile_no: m.mobile_no,
      msg_type:  m.msg_type,
      subject:   m.subject,
      message:   m.message,
      status:    m.status,
      req_date:  new Date(m.req_date),
      is_active: m.is_active,
    };
    this.errors    = {};
    this.submitted = false;
    this.editingId = m.id;
    this.isEditing.set(true);
    this.showViewModal.set(false);
    this.showFormModal.set(true);
  }

  openView(m: ContactMsg): void {
    this.viewTarget.set(m);
    this.showViewModal.set(true);
    if (m.status === 'New') this.setStatus(m, 'In Progress');
  }

  save(): void {
    this.submitted = true;
    if (!this.validateForm()) return;

    const payload: Omit<ContactMsg, 'id'> = {
      full_name: this.form.full_name.trim(),
      email_id:  this.form.email_id.trim().toLowerCase(),
      mobile_no: this.form.mobile_no.trim(),
      msg_type:  this.form.msg_type,
      subject:   this.form.subject.trim(),
      message:   this.form.message.trim(),
      status:    this.form.status,
      req_date:  this.form.req_date instanceof Date ? this.form.req_date : new Date(),
      is_active: this.form.is_active,
    };

    if (this.isEditing()) {
      this.doUpdate(this.base, payload, this.editingId);
      this.messages.update(list =>
        list.map(m => m.id === this.editingId ? { ...m, ...payload } : m)
      );
      this.saveSuccess.set('Message updated successfully.');
    } else {
      this.doSave(this.base, payload);
      const row: ContactMsg = { id: this.nextId++, ...payload };
      this.messages.update(list => [row, ...list]);
      this.saveSuccess.set('Contact message logged successfully.');
    }

    this.showFormModal.set(false);
    setTimeout(() => this.saveSuccess.set(''), 2200);
  }

  confirmDelete(m: ContactMsg): void {
    this.deleteTarget.set(m);
    this.showViewModal.set(false);
    this.showDeleteModal.set(true);
  }

  executeDelete(): void {
    const t = this.deleteTarget();
    if (!t) return;

    this.doDelete(this.base, t.id);
    this.messages.update(list => list.filter(m => m.id !== t.id));
    this.showDeleteModal.set(false);
    this.deleteTarget.set(null);
    if (this.currentPage() > this.totalPages()) this.currentPage.set(Math.max(1, this.totalPages()));
    this.saveSuccess.set('Message deleted.');
    setTimeout(() => this.saveSuccess.set(''), 2200);
  }

  setStatus(m: ContactMsg, status: string): void {
    this.messages.update(list => list.map(x => x.id === m.id ? { ...x, status } : x));
    const viewing = this.viewTarget();
    if (viewing?.id === m.id) this.viewTarget.set({ ...viewing, status });
  }

  toggleActive(m: ContactMsg): void {
    this.messages.update(list =>
      list.map(x => x.id === m.id ? { ...x, is_active: !x.is_active } : x)
    );

    this.messages()?.filter((obj: any) => {
      if(obj.id==m.id) {
        this.doUpdate(this.base, obj, m.id);
      }
    });
  }

  // ──────────────────────────────────────────────────────────
  // FILTER / SORT / PAGE
  // ──────────────────────────────────────────────────────────
  sortBy(field: keyof ContactMsg): void {
    if (this.sortField() === field) this.sortAsc.update(v => !v);
    else { this.sortField.set(field); this.sortAsc.set(true); }
    this.currentPage.set(1);
  }

  onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.onFilterChange();
  }

  onPageSizeChange(size: number | string): void {
    this.pageSize.set(Number(size));
    this.currentPage.set(1);
  }

  onFilterChange(): void { this.currentPage.set(1); }

  setTypeFilter(v: 'all' | MsgType): void {
    this.filterType.set(v);
    this.onFilterChange();
  }

  setStatusFilter(v: 'all' | MsgStatus): void {
    this.filterStatus.set(v);
    this.onFilterChange();
  }

  resetFilters(): void {
    this.searchQuery.set('');
    this.filterStatus.set('all');
    this.filterType.set('all');
    this.filterActive.set('all');
    this.onFilterChange();
  }

  goToPage(p: number): void {
    if (p >= 1 && p <= this.totalPages()) this.currentPage.set(p);
  }

  // ──────────────────────────────────────────────────────────
  // VALIDATION
  // ──────────────────────────────────────────────────────────
  validateForm(): boolean {
    this.errors = {};
    const name  = this.form.full_name.trim();
    const email = this.form.email_id.trim();
    const mob   = this.form.mobile_no.trim();
    const subj  = this.form.subject.trim();
    const msg   = this.form.message.trim();

    if (!name) this.errors.full_name = 'Full name is required.';
    else if (name.length < 2) this.errors.full_name = 'Name must be at least 2 characters.';

    if (!email) this.errors.email_id = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) this.errors.email_id = 'Enter a valid email address.';

    if (!mob) this.errors.mobile_no = 'Mobile number is required.';
    else if (!/^[0-9]{10}$/.test(mob)) this.errors.mobile_no = 'Enter a 10-digit mobile number.';

    if (!this.form.msg_type) this.errors.msg_type = 'Message type is required.';
    if (!subj) this.errors.subject = 'Subject is required.';
    if (!msg) this.errors.message = 'Message is required.';
    else if (msg.length < 10) this.errors.message = 'Message must be at least 10 characters.';
    if (!this.form.status) this.errors.status = 'Status is required.';

    return Object.keys(this.errors).length === 0;
  }

  // ──────────────────────────────────────────────────────────
  // HELPERS
  // ──────────────────────────────────────────────────────────
  formatDate(d: Date): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  formatDateTime(d: Date): string {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  }

  initials(name: string): string {
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  }

  sortIcon(field: keyof ContactMsg): string {
    if (this.sortField() !== field) return 'bi-arrow-down-up text-muted';
    return this.sortAsc() ? 'bi-sort-down-alt text-primary' : 'bi-sort-up text-primary';
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      'New':         'st-new',
      'In Progress': 'st-progress',
      'Replied':     'st-replied',
      'Closed':      'st-closed',
    };
    return map[status] ?? 'st-closed';
  }

  typeClass(type: string): string {
    const map: Record<string, string> = {
      Enquiry:   'tp-enquiry',
      Admission: 'tp-admission',
      Complaint: 'tp-complaint',
      Feedback:  'tp-feedback',
      Support:   'tp-support',
      Other:     'tp-other',
    };
    return map[type] ?? 'tp-other';
  }

  truncate(text: string, n = 48): string {
    return text.length > n ? text.slice(0, n).trimEnd() + '…' : text;
  }

  blankForm(): Omit<ContactMsg, 'id'> {
    return {
      full_name: '',
      email_id:  '',
      mobile_no: '',
      msg_type:  'Enquiry',
      subject:   '',
      message:   '',
      status:    'New',
      req_date:  new Date(),
      is_active: true,
    };
  }

  // ──────────────────────────────────────────────────────────
  // FEED DATA — 20 contact_msgs rows
  // ──────────────────────────────────────────────────────────
  // private buildFeed(): ContactMsg[] {
  //   const rows: Omit<ContactMsg, 'id'>[] = [
  //     {
  //       full_name: 'Meera Iyer', email_id: 'meera.iyer@gmail.com', mobile_no: '9876501101',
  //       msg_type: 'Enquiry', subject: 'School transport for Class III',
  //       message: 'We recently moved to Sector 21. Could you confirm whether the school bus covers that area for Class III students, and what the annual transport fee is?',
  //       status: 'New', req_date: new Date(2026, 8, 18, 9, 15), is_active: true,
  //     },
  //     {
  //       full_name: 'Rohit Malhotra', email_id: 'rohit.malhotra@outlook.com', mobile_no: '9811122233',
  //       msg_type: 'Admission', subject: 'Admission enquiry for Class VI – 2026-27',
  //       message: 'We would like to apply for Class VI for our son Kabir. Please share the admission schedule, documents required, and whether an entrance test is conducted.',
  //       status: 'New', req_date: new Date(2026, 8, 17, 16, 42), is_active: true,
  //     },
  //     {
  //       full_name: 'Anjali Deshmukh', email_id: 'anjali.d@yahoo.com', mobile_no: '9922003344',
  //       msg_type: 'Complaint', subject: 'Bus RT-04 running consistently late',
  //       message: 'For the past two weeks bus RT-04 has been 20–25 minutes late in the morning. My daughter misses the first period. Kindly look into the route timing.',
  //       status: 'New', req_date: new Date(2026, 8, 17, 8, 5), is_active: true,
  //     },
  //     {
  //       full_name: 'Vikram Patel', email_id: 'vikram.patel@gmail.com', mobile_no: '9001122334',
  //       msg_type: 'Support', subject: 'Unable to log in to parent portal',
  //       message: 'I registered last week but the parent portal keeps saying invalid credentials. Student ID is S-2024-1187. Please reset access or share the correct username.',
  //       status: 'New', req_date: new Date(2026, 8, 16, 19, 28), is_active: true,
  //     },
  //     {
  //       full_name: 'Sunita Singh', email_id: 'sunita.singh@rediffmail.com', mobile_no: '9876543210',
  //       msg_type: 'Feedback', subject: 'Appreciation for Science exhibition',
  //       message: 'The Class X science exhibition on 12 September was excellent. The students were well prepared and the judges’ feedback was encouraging. Please convey our thanks to the science faculty.',
  //       status: 'New', req_date: new Date(2026, 8, 16, 11, 10), is_active: true,
  //     },
  //     {
  //       full_name: 'Arjun Sharma', email_id: 'arjun.sharma@gmail.com', mobile_no: '9810012345',
  //       msg_type: 'Enquiry', subject: 'Fee concession for sibling admission',
  //       message: 'Our elder daughter is already in Class VIII. We are applying for Class I for our younger son. Is there a sibling concession on tuition fees? If yes, what documents are needed?',
  //       status: 'New', req_date: new Date(2026, 8, 15, 14, 55), is_active: true,
  //     },
  //     {
  //       full_name: 'Priya Sharma', email_id: 'priya.sharma@edusmart.in', mobile_no: '9820098765',
  //       msg_type: 'Support', subject: 'Correction in student date of birth',
  //       message: 'The ERP shows my son’s date of birth as 12-Mar-2014. The birth certificate has 12-May-2014. Please advise the process to correct this in the records.',
  //       status: 'In Progress', req_date: new Date(2026, 8, 14, 10, 20), is_active: true,
  //     },
  //     {
  //       full_name: 'Farhan Qureshi', email_id: 'farhan.q@gmail.com', mobile_no: '9700011223',
  //       msg_type: 'Admission', subject: 'Mid-session transfer from CBSE school',
  //       message: 'We are relocating from Lucknow in October. Can Class IX admission be considered mid-session? Transfer certificate and previous mark sheets are ready.',
  //       status: 'In Progress', req_date: new Date(2026, 8, 13, 17, 40), is_active: true,
  //     },
  //     {
  //       full_name: 'Kavita Nair', email_id: 'kavita.nair@gmail.com', mobile_no: '9898989898',
  //       msg_type: 'Complaint', subject: 'Canteen hygiene concern',
  //       message: 'Several Class VII students reported an upset stomach after buying sandwiches on 11 September. Please review canteen hygiene and supplier quality.',
  //       status: 'In Progress', req_date: new Date(2026, 8, 12, 13, 8), is_active: true,
  //     },
  //     {
  //       full_name: 'Deepak Yadav', email_id: 'deepak.yadav@hotmail.com', mobile_no: '9123456780',
  //       msg_type: 'Enquiry', subject: 'Hostel vacancy for Class XI Science',
  //       message: 'Is a hostel seat available for Class XI Science (PCM) for 2026-27? Please share mess charges, room type options, and warden contact details.',
  //       status: 'In Progress', req_date: new Date(2026, 8, 11, 9, 45), is_active: true,
  //     },
  //     {
  //       full_name: 'Neha Joshi', email_id: 'neha.joshi@gmail.com', mobile_no: '9988776655',
  //       msg_type: 'Feedback', subject: 'New library hours are helpful',
  //       message: 'Extending library hours till 6 PM has been very useful for Class XII students preparing for boards. Thank you for considering the PTA request.',
  //       status: 'Replied', req_date: new Date(2026, 8, 10, 18, 12), is_active: true,
  //     },
  //     {
  //       full_name: 'Sanjay Gupta', email_id: 'sanjay.gupta@gmail.com', mobile_no: '9012345678',
  //       msg_type: 'Support', subject: 'Fee receipt not generated after UPI payment',
  //       message: 'I paid ₹18,500 via PhonePe on 8 September (UTR 329981445512). The payment module still shows pending and no receipt was emailed. Kindly reconcile.',
  //       status: 'Replied', req_date: new Date(2026, 8, 8, 20, 3), is_active: true,
  //     },
  //     {
  //       full_name: 'Lakshmi Rao', email_id: 'lakshmi.rao@yahoo.com', mobile_no: '9845011122',
  //       msg_type: 'Admission', subject: 'Documents for Class I admission',
  //       message: 'Please confirm whether Aadhaar, birth certificate, and two photographs are sufficient for Class I, or if a residence proof is also mandatory.',
  //       status: 'Replied', req_date: new Date(2026, 8, 7, 11, 30), is_active: true,
  //     },
  //     {
  //       full_name: 'Amit Verma', email_id: 'amit.verma@gmail.com', mobile_no: '9311122233',
  //       msg_type: 'Enquiry', subject: 'After-school robotics club',
  //       message: 'Does the school run a robotics / STEM club after school hours for Class VI–VIII? If yes, what is the weekly schedule and fee?',
  //       status: 'Replied', req_date: new Date(2026, 8, 5, 15, 18), is_active: true,
  //     },
  //     {
  //       full_name: 'Pooja Nair', email_id: 'pooja.nair@gmail.com', mobile_no: '9765432109',
  //       msg_type: 'Other', subject: 'Request to book auditorium for alumni meet',
  //       message: 'The 2015 batch would like to host a small alumni gathering on 4 October. Can the auditorium be reserved from 4 PM to 7 PM? Expected attendance is 80.',
  //       status: 'Replied', req_date: new Date(2026, 8, 4, 12, 0), is_active: true,
  //     },
  //     {
  //       full_name: 'Rajesh Kumar', email_id: 'rajesh.kumar@edusmart.in', mobile_no: '9800112233',
  //       msg_type: 'Support', subject: 'Staff ID card reprint',
  //       message: 'My staff ID card is damaged. Employee ID EMP-041. Please let me know the reprint process and whether a photo needs to be submitted again.',
  //       status: 'Replied', req_date: new Date(2026, 8, 2, 9, 50), is_active: true,
  //     },
  //     {
  //       full_name: 'Harpreet Kaur', email_id: 'harpreet.kaur@gmail.com', mobile_no: '9876012345',
  //       msg_type: 'Complaint', subject: 'Playground equipment needs repair',
  //       message: 'The swing set near Block B has a loose chain. Several parents flagged this during drop-off. Requesting urgent inspection before someone gets hurt.',
  //       status: 'Closed', req_date: new Date(2026, 7, 28, 8, 22), is_active: true,
  //     },
  //     {
  //       full_name: 'Mohammed Irfan', email_id: 'irfan.m@gmail.com', mobile_no: '9090901234',
  //       msg_type: 'Admission', subject: 'NRI quota enquiry for Class XI Commerce',
  //       message: 'We currently reside in Dubai. Is there an NRI quota for Class XI Commerce? Please share eligibility, fee structure in INR, and the last date to apply.',
  //       status: 'Closed', req_date: new Date(2026, 7, 22, 21, 5), is_active: true,
  //     },
  //     {
  //       full_name: 'Sneha Kapoor', email_id: 'sneha.kapoor@gmail.com', mobile_no: '9818090909',
  //       msg_type: 'Feedback', subject: 'Duplicate enquiry – please ignore',
  //       message: 'I accidentally submitted the same transport enquiry twice yesterday. Please ignore the earlier ticket. Sorry for the inconvenience.',
  //       status: 'Closed', req_date: new Date(2026, 7, 18, 10, 11), is_active: false,
  //     },
  //     {
  //       full_name: 'Test Parent', email_id: 'test.parent@example.com', mobile_no: '9000000000',
  //       msg_type: 'Other', subject: 'Spam / test submission',
  //       message: 'This was a test submission from the old website contact form. Marked inactive after verification.',
  //       status: 'Closed', req_date: new Date(2026, 6, 30, 7, 0), is_active: false,
  //     },
  //   ];

  //   return rows.map((row, i) => ({ id: i + 1, ...row }));
  // }

  // ── ACTIONS ─────────────────────────────────────────
  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any, myid: number) {
    this.loading = true;
    // this.submitSuccess = true;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

}
